import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import TryFreeCTA from "../../../Components/TryFreeCTA/TryFreeCTA";
import styles from "./BookAppointment.module.css";
import Heading from "../../../Components/Heading/Heading";

// Each swatch is a different business vertical, styled in its own brand colour —
// showing the same Stylon storefront adapts to any business + palette.
import arNails  from "../../../assets/biz_nails_ar.png";
import enNails  from "../../../assets/biz_nails_en.png";
import heNails  from "../../../assets/biz_nails_he.png";
import arDental from "../../../assets/biz_dental_ar.png";
import enDental from "../../../assets/biz_dental_en.png";
import heDental from "../../../assets/biz_dental_he.png";
import arBarber from "../../../assets/biz_barber_ar.png";
import enBarber from "../../../assets/biz_barber_en.png";
import heBarber from "../../../assets/biz_barber_he.png";
import arHair   from "../../../assets/biz_hair_ar.png";
import enHair   from "../../../assets/biz_hair_en.png";
import heHair   from "../../../assets/biz_hair_he.png";

const THEMES = [
  { key: "nails",  color: "#ec4899" }, // pink
  { key: "dental", color: "#2d8cff" }, // blue
  { key: "barber", color: "#1a1a1a" }, // black
  { key: "hair",   color: "#8b5cf6" }, // purple
];

const IMAGES = {
  ar: { nails: arNails, dental: arDental, barber: arBarber, hair: arHair },
  he: { nails: heNails, dental: heDental, barber: heBarber, hair: heHair },
  en: { nails: enNails, dental: enDental, barber: enBarber, hair: enHair },
};

const AUTO_INTERVAL = 3000;
const FADE_DURATION = 220;

const BookAppointment = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const images = IMAGES[lang] || IMAGES.en;

  const [activeTheme, setActiveTheme] = useState("nails");
  const [visibleTheme, setVisibleTheme] = useState("nails"); // which business is shown
  const [fading, setFading] = useState(false);
  const isManualRef = useRef(false);
  const timerRef = useRef(null);

  const switchTo = useCallback((key) => {
    setFading(true);
    setTimeout(() => {
      setVisibleTheme(key);
      setFading(false);
    }, FADE_DURATION);
    setActiveTheme(key);
  }, []);

  const handleClick = (key) => {
    isManualRef.current = true;
    clearInterval(timerRef.current);
    switchTo(key);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (isManualRef.current) return;
      setActiveTheme(prev => {
        const idx = THEMES.findIndex(t => t.key === prev);
        const nextKey = THEMES[(idx + 1) % THEMES.length].key;
        setFading(true);
        setTimeout(() => {
          setVisibleTheme(nextKey);
          setFading(false);
        }, FADE_DURATION);
        return nextKey;
      });
    }, AUTO_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <div className={`container ${styles.parent}`} data-aos="fade-up" data-aos-duration="500">
      {/* shown only on mobile, above the image */}
      <div className={styles.mobileHeading}>
        <Heading text={t("bookAppointment.heading")} />
      </div>

      <section className={styles.child1}>
        <div
          className={styles.phoneFrame}
          style={{ "--theme-color": THEMES.find(t => t.key === activeTheme)?.color }}
        >
          <div className={styles.phoneGlass} />
          <img
            src={images[visibleTheme]}
            alt="app website"
            className={`${styles.phoneImg} ${fading ? styles.fadeOut : styles.fadeIn}`}
          />
        </div>

        <div className={styles.swatches}>
          {THEMES.map(({ key, color }) => (
            <button
              key={key}
              type="button"
              className={`${styles.swatch} ${activeTheme === key ? styles.swatchActive : ""}`}
              style={{ "--swatch-color": color }}
              onClick={() => handleClick(key)}
              aria-label={key}
            />
          ))}
        </div>
      </section>

      <section className={styles.child2}>
        <div className={styles.headingBox}><Heading text={t("bookAppointment.heading")} /></div>
        <div className={styles.paraBox}>
          <p>{t("bookAppointment.paragraph")}</p>
        </div>
        <TryFreeCTA centered={false} section="book_appointment" />
      </section>
    </div>
  );
};

export default BookAppointment;
